import { useEffect, useMemo, useRef, useState } from "react";
import { apiService } from "../api/apiService";

/**
 * [A11-30] Wyszukiwanie pracownika po stronie SERWERA.
 *
 * BYŁO ŹLE: cztery ekrany (kreator protokołu, szuflada licencji, TeamViewer, dyski sieciowe)
 * wołały `searchProtocolUsers("")` przy otwarciu i pobierały CAŁĄ kadrę firmy razem z firmą
 * i przypisaniami działowymi, po czym filtrowały ją w przeglądarce.
 * SKUTEK DLA UŻYTKOWNIKA: otwarcie każdego z tych ekranów zawieszało się na kilka sekund
 * („Ładowanie pracowników…"), a przy większej kadrze potrafiło się nie doczekać.
 *
 * NAPRAWA: fraza leci do serwera, serwer oddaje dopasowania z limitem. Wybór osoby działa jak dotąd
 * — także tej z KOŃCA alfabetu, czego samo obcięcie listy do pierwszych 50 nazwisk by nie dało.
 *
 * Zapytanie jest opóźnione (debounce), a odpowiedzi spóźnione są odrzucane po numerze żądania,
 * żeby wolniejsza odpowiedź na starszą frazę nie nadpisała świeższych wyników.
 */
export default function useServerUserSearch({
    enabled = true,
    query = "",
    limit = 8,
    debounceMs = 250,
    department = "",
    position = "",
    sort = "",
} = {}) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const zadanieRef = useRef(0);

    // Parametry sklejone w string — dzięki temu useEffect nie zapętla się na tożsamości obiektu.
    const parametry = useMemo(
        () => JSON.stringify({ query: (query || "").trim(), limit, department, position, sort }),
        [query, limit, department, position, sort]
    );

    useEffect(() => {
        if (!enabled) {
            setUsers([]);
            setTotal(0);
            setLoading(false);
            return undefined;
        }

        const p = JSON.parse(parametry);
        const mojNumer = ++zadanieRef.current;
        setLoading(true);

        const timer = setTimeout(() => {
            apiService
                .searchProtocolUsers(p.query, {
                    limit: p.limit,
                    department: p.department || undefined,
                    position: p.position || undefined,
                    sort: p.sort || undefined,
                })
                .then((res) => {
                    // Spóźniona odpowiedź na starszą frazę — ignorujemy, inaczej lista „skakałaby" wstecz.
                    if (mojNumer !== zadanieRef.current) return;
                    const dane = Array.isArray(res.data) ? res.data : [];
                    setUsers(dane);
                    const naglowek = Number(res.headers?.["x-total-count"]);
                    setTotal(Number.isFinite(naglowek) && naglowek >= 0 ? naglowek : dane.length);
                })
                .catch(() => {
                    if (mojNumer !== zadanieRef.current) return;
                    setUsers([]);
                    setTotal(0);
                })
                .finally(() => {
                    if (mojNumer === zadanieRef.current) setLoading(false);
                });
        }, debounceMs);

        return () => clearTimeout(timer);
    }, [enabled, parametry, debounceMs]);

    return {
        users,
        loading,
        total,
        /** Czy serwer miał więcej dopasowań, niż zmieściło się w odpowiedzi. */
        truncated: total > users.length,
    };
}
